import { React, useState, useEffect, Drawer, DialogActions, Box, Typography, Divider, IconButton, ArrowBackIosIcon, Default_Text_Color } from '../../utils/globalImports';
import { CustomButton, CustomTextField, CustomRadioButton, validationMessages } from '../../utils/globalImports';

interface CategoryData {
  categoryName: string;
  description: string;
  status: string;
}

interface CustomAddCategoryModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: CategoryData, setBackendErrors?: (errors: any) => void) => void;
  categoryData?: CategoryData;
  handleContentChange:(state:boolean)=>void
}

const validationMessageStyle = {
  color: "rgba(255, 0, 0, 0.99)",
  fontSize: "11px",
  paddingLeft: "10px",
  paddingBottom: "10px",
};

const CustomAddCategoryModal: React.FC<CustomAddCategoryModalProps> = ({ open, onClose, onSubmit, categoryData,handleContentChange }) => {
  const [formData, setFormData] = useState<CategoryData>({
    categoryName: '',
    description: '',
    status: 'Active',
  });

  const [errors, setErrors] = useState({
    categoryName: '',
    description: '',
  });

  const [backendErrors, setBackendErrors] = useState<{ [key: string]: string }>({});

  // Validate input fields
  const validate = () => {
    let isValid = true;
    const errors = { categoryName: '', description: '' };

    if (!formData.categoryName.trim()) {
      errors.categoryName = validationMessages.common_required("Client Category Name");
      isValid = false;
    } else if (formData.categoryName.length > 255) {
      errors.categoryName = validationMessages.common_characterLimit("Client Category Name", 255);
      isValid = false;
    } else if (!/^[A-Za-z\s]+$/.test(formData.categoryName)) {
      errors.categoryName = validationMessages.common_invalidString("Client Category Name");
      isValid = false;
    }

    if (formData.description.length > 5000) {
      errors.description = validationMessages.common_characterLimit("Description", 5000);
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (validate()) {
      await onSubmit(formData, setBackendErrors);
    }
  };

  // Populate fields if editing a category
  useEffect(() => {
    if (open) {
      setFormData({
        categoryName: categoryData?.categoryName || '',
        description: categoryData?.description || '',
        status: categoryData?.status || 'Active',
      });

      setErrors({
        categoryName: '',
        description: ''
      });
      setBackendErrors({});
    }
  }, [open, categoryData]);


  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    handleContentChange(true);
  };

  const handleNavigate = () => {
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        width: 350,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 500,
          height: '100%',
          paddingLeft: 2,
          paddingRight: 2,
          paddingTop: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.86)',
        },
      }}
    >
      <Box>
        <Typography
          sx={{
            color: Default_Text_Color,
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            mt: 2,
            position: 'relative',
            fontWeight: 500,
            fontSize: 22
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              left: 0,
              width: '40px',
              height: '40px',
              backgroundColor: 'transparent',
            }}
            onClick={handleNavigate}
          >
            <ArrowBackIosIcon sx={{ color: '#1e66ab', fontSize: '15px' }} />
          </IconButton>
          {`${categoryData ? 'Edit' : 'Add'} Client Category`}
        </Typography>

        <Divider sx={{ mb: 3, mx: "-15px" }} />

        {/* Client Category Name */}
        <CustomTextField
          label="Client Category Name"
          name="categoryName"
          value={formData.categoryName}
          onChange={handleChange}
          sx={{ mb: 2, mt:1.5, backgroundColor: '#fff' }}
          required={true}
        />
        {errors.categoryName && (
          <Typography style={validationMessageStyle} role="alert">
            {errors.categoryName}
          </Typography>
        )}
        {backendErrors.name && <Typography style={validationMessageStyle}>{backendErrors.name}</Typography>}

        {/* Description */}
        <CustomTextField
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          sx={{
            mb: 2, mt:1.5, backgroundColor: '#fff',
            '& .MuiInputBase-root': {
              height: 'auto',
            },
            '& .MuiOutlinedInput-input': {
              minHeight: 100,
              padding: '10px',
            },
          }}
          required={false}
          isTextArea={true}
        />
        {errors.description && (
          <Typography style={validationMessageStyle} role="alert">
            {errors.description}
          </Typography>
        )}

        {/* Status */}
        <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1, mt: 1 }}>Status</Typography>
        <Box sx={{ display: 'flex' }}>
          <CustomRadioButton
            label="Active"
            value="Active"
            name="status"
            checked={formData.status === 'Active'}
            onChange={handleChange}
            required={true}
          />
          <CustomRadioButton
            label="Inactive"
            value="Inactive"
            name="status"
            checked={formData.status === 'Inactive'}
            onChange={handleChange}
            required={true}
          />
        </Box>

        {/* Submit Button */}
        <DialogActions>
          <CustomButton
            capsule
            sx={{
              textTransform: 'none',
              width: '282px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              mr: 'auto',
              ml: 'auto',
              mt: 2,
            }}
            onClick={handleSubmit}
          >
            {categoryData ? 'Update Client Category' : 'Add Client Category'}
          </CustomButton>
        </DialogActions>
      </Box>
    </Drawer>
  );
};

export default CustomAddCategoryModal;
